import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { theme } from '../../styles/theme';

const BannerContainer = styled.div`
  position: fixed;
  top: 280px; /* Position below announcement banner (27px) + navbar (120px) + opening status banner (60px) + some spacing */
  left: 0;
  right: 0;
  background: #0a1530;
  color: white;
  padding: 0.8rem 2rem;
  z-index: 100; /* Lower than navbar but higher than content */
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border-bottom: 2px solid ${props => props.theme.colors.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  animation: slideDown 0.5s ease-out forwards;
  
  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }


  @media (max-width: 1024px) {
    top: 250px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileInfoButton = styled.button`
  display: none;
  position: fixed;
  bottom: 20px;
  right: 80px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #0a1530;
  border: 2px solid ${props => props.theme.colors.accent};
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 1000;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ModalOverlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1001;
  animation: fadeIn 0.3s ease-out forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
  }
`;

const ModalContent = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #0a1530;
  color: white;
  padding: 1.5rem;
  border-radius: 20px 20px 0 0;
  transform: translateY(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.3s ease-out;
  z-index: 1002;

  @media (max-width: 768px) {
    display: block;
  }
`;

const BannerContent = styled.div`
  max-width: 1200px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
`;

const InfoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const Icon = styled.div`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.accent};
`;

const TextContent = styled.div`
  font-size: 1rem;
  line-height: 1.4;
  
  p {
    margin: 0;
  }
`;

const ImportantText = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.accent};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;


const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.5rem;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ClosureBanner = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [shouldShow, setShouldShow] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const closureEndDate = new Date('2025-10-16');
    const currentDate = new Date();
    
    // Hide banner if current date is after closure end date
    if (currentDate > closureEndDate) {
      setShouldShow(false);
    }


  }, []);

  if (!isVisible || !shouldShow) return null;

  return (
    <>
      <BannerContainer>
        <BannerContent>
          <InfoSection>
            <Icon>
              <FontAwesomeIcon icon={faInfoCircle} />
            </Icon>
            <TextContent>
              <p>
                <ImportantText>Important Notice:</ImportantText> Belvoir Castle is closed on <ImportantText>16th October 2025</ImportantText> due to a private event. Her Grace The Duchess of Rutland is hosting an exclusive fashion show and lunch in support of Marie Curie.
              </p>
            </TextContent>
          </InfoSection>
          <CloseButton onClick={() => setIsVisible(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </BannerContent>
      </BannerContainer>

      <MobileInfoButton onClick={() => setIsModalOpen(true)}>
        <FontAwesomeIcon icon={faInfoCircle} />
      </MobileInfoButton>

      <ModalOverlay $isOpen={isModalOpen} onClick={() => setIsModalOpen(false)}>
        <ModalContent $isOpen={isModalOpen} onClick={e => e.stopPropagation()}>
          <BannerContent>
            <InfoSection>
              <Icon>
                <FontAwesomeIcon icon={faInfoCircle} />
              </Icon>
              <TextContent>
                <p>
                  <ImportantText>Important Notice:</ImportantText> Belvoir Castle is closed on <ImportantText>16th October 2025</ImportantText> due to a private event. Her Grace The Duchess of Rutland is hosting an exclusive fashion show and lunch in support of Marie Curie.
                </p>
              </TextContent>
            </InfoSection>
            <CloseButton onClick={() => setIsModalOpen(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </CloseButton>
          </BannerContent>
        </ModalContent>
      </ModalOverlay>
    </>
  );
};

export default ClosureBanner; 